import { z } from "zod";
import { PointVectorSchema } from "./catalog.js";
import { ProviderFormatRetryTraceSchema } from "./agentSpec.js";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, SourceRefSchema } from "./source.js";
export const ParityLineStatusSchema = z.enum([
    "exact",
    "quantity_partial",
    "kind_partial",
    "missing",
    "extra"
]);
export const ParityLineSchema = z
    .object({
    canonicalPartNo: NonEmptyStringSchema,
    kind: NonEmptyStringSchema.optional(),
    expectedQty: z.number().int().nonnegative(),
    actualQty: z.number().int().nonnegative(),
    status: ParityLineStatusSchema,
    mismatchId: IdSchema.optional(),
    stablePath: NonEmptyStringSchema.optional()
})
    .strict();
const ProviderFormatRetryCallCounterSchema = z
    .object({
    callId: NonEmptyStringSchema,
    maxAttempts: z.number().int().positive(),
    attemptCount: z.number().int().positive(),
    recovered: z.boolean(),
    exhausted: z.boolean(),
    failureCount: z.number().int().nonnegative(),
    backoffCount: z.number().int().nonnegative()
})
    .strict();
const emptyProviderFormatRetryCounters = () => ({
    tracedCallCount: 0,
    recoveredCount: 0,
    exhaustedCount: 0,
    failureCount: 0,
    backoffCount: 0,
    calls: [],
    traces: []
});
const ProviderFormatRetryCountersSchema = z
    .object({
    tracedCallCount: z.number().int().nonnegative(),
    recoveredCount: z.number().int().nonnegative(),
    exhaustedCount: z.number().int().nonnegative(),
    failureCount: z.number().int().nonnegative(),
    backoffCount: z.number().int().nonnegative(),
    calls: z.array(ProviderFormatRetryCallCounterSchema),
    traces: z.array(ProviderFormatRetryTraceSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    const expected = {
        tracedCallCount: value.calls.length,
        recoveredCount: value.calls.filter((call) => call.recovered).length,
        exhaustedCount: value.calls.filter((call) => call.exhausted).length,
        failureCount: value.calls.reduce((total, call) => total + call.failureCount, 0),
        backoffCount: value.calls.reduce((total, call) => total + call.backoffCount, 0)
    };
    for (const [field, expectedValue] of Object.entries(expected)) {
        if (value[field] !== expectedValue) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field} must be derived from provider-format retry call rows`,
                path: [field]
            });
        }
    }
});
export const ParityDimensionKeySchema = z.enum([
    "controller_allocation",
    "derived_devices",
    "screens_ui",
    "sensors",
    "infrastructure",
    "point_ledger",
    "risks_blocking"
]);
const PARITY_DIMENSION_KEYS = [...ParityDimensionKeySchema.options].sort();
export const ParityDimensionResultSchema = z
    .object({
    key: ParityDimensionKeySchema,
    scored: z.boolean(),
    deferredReason: NonEmptyStringSchema.optional(),
    lines: z.array(ParityLineSchema),
    exactLineRate: z.number().min(0).max(1),
    quantityOverlapRate: z.number().min(0).max(1),
    kindCoverageRate: z.number().min(0).max(1).optional(),
    passBar: z
        .object({
        exactLineRate: z.number().min(0).max(1).optional(),
        quantityOverlapRate: z.number().min(0).max(1).optional(),
        kindCoverageRate: z.number().min(0).max(1).optional()
    })
        .strict(),
    passed: z.boolean()
})
    .strict()
    .superRefine((value, ctx) => {
    const expectedLines = value.lines.filter((line) => line.status !== "extra");
    const hasExtra = value.lines.some((line) => line.status === "extra");
    const rate = (numerator, denominator, extraPresent = false) => (denominator === 0 ? (extraPresent ? 0 : 1) : numerator / denominator);
    const expectedLineCount = expectedLines.length;
    const expectedQuantity = expectedLines.reduce((total, line) => total + line.expectedQty, 0);
    const exactLineCount = expectedLines.filter((line) => line.status === "exact").length;
    const overlapQuantity = expectedLines
        .filter((line) => ["exact", "quantity_partial"].includes(line.status))
        .reduce((total, line) => total + Math.min(line.expectedQty, line.actualQty), 0);
    const kindCoveredCount = expectedLines.filter((line) => ["exact", "quantity_partial", "kind_partial"].includes(line.status)).length;
    const expectedRates = {
        exactLineRate: rate(exactLineCount, expectedLineCount, hasExtra),
        quantityOverlapRate: rate(overlapQuantity, expectedQuantity, hasExtra),
        ...(value.key === "infrastructure"
            ? {
                kindCoverageRate: rate(kindCoveredCount, expectedLineCount, hasExtra)
            }
            : {})
    };
    const nearlyEqual = (left, right) => Math.abs(left - right) <= Number.EPSILON * 16;
    for (const [field, expectedRate] of Object.entries(expectedRates)) {
        const actualRate = value[field];
        if (typeof actualRate !== "number" || !nearlyEqual(actualRate, expectedRate)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field} must be derived from dimension line rows`,
                path: [field]
            });
        }
    }
    if (value.key !== "infrastructure" && value.kindCoverageRate !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "kindCoverageRate is only defined for infrastructure dimensions",
            path: ["kindCoverageRate"]
        });
    }
    const expectedPassed = !value.scored ||
        ((value.passBar.exactLineRate === undefined ||
            value.exactLineRate >= value.passBar.exactLineRate) &&
            (value.passBar.quantityOverlapRate === undefined ||
                value.quantityOverlapRate >= value.passBar.quantityOverlapRate) &&
            (value.passBar.kindCoverageRate === undefined ||
                (value.kindCoverageRate ?? 0) >= value.passBar.kindCoverageRate));
    if (value.passed !== expectedPassed) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "dimension passed must be derived from rates and passBar",
            path: ["passed"]
        });
    }
});
export const ParityCaseRunStatusSchema = z.enum([
    "complete",
    "draft_not_certified",
    "partial",
    "failed_to_run"
]);
export const ParityApartmentTypeOutcomeSchema = z.enum([
    "verified",
    "draft_not_certified",
    "failed"
]);
export const PlaceholderSkuSchema = z
    .object({
    partNo: NonEmptyStringSchema,
    itemKind: NonEmptyStringSchema,
    reason: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema).min(1),
    policyQuestionId: NonEmptyStringSchema.optional()
})
    .strict();
export const MismatchClassificationSchema = z.enum([
    "better_than_delivered",
    "source_data_gap",
    "rule_gap",
    "ai_error",
    "golden_error",
    "needs_rich_adjudication",
    "unexplained"
]);
export const SourceDataGapSubtypeSchema = z.enum([
    "source_missing",
    "source_misextracted"
]);
export const MismatchEvidenceSchema = z
    .object({
    kind: z.enum([
        "source_doc",
        "rule_ref",
        "agent_trace",
        "golden_note",
        "fixture_field"
    ]),
    ref: NonEmptyStringSchema,
    locator: NonEmptyStringSchema.optional()
})
    .strict();
export const MismatchStatusSchema = z.enum([
    "unexplained",
    "open",
    "fix_dispatched",
    "closed_fixed",
    "closed_adjudicated",
    "closed_accepted",
    "reverted"
]);
export const TERMINAL_MISMATCH_STATUSES = [
    "closed_fixed",
    "closed_adjudicated",
    "closed_accepted"
];
const isTerminalStatus = (status) => TERMINAL_MISMATCH_STATUSES.includes(status);
export const ParityMismatchSchema = z
    .object({
    id: IdSchema,
    runId: IdSchema,
    benchmarkCaseId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    dimension: ParityDimensionKeySchema,
    path: NonEmptyStringSchema,
    stablePath: NonEmptyStringSchema.optional(),
    expected: z.unknown(),
    actual: z.unknown(),
    classification: MismatchClassificationSchema,
    subtype: SourceDataGapSubtypeSchema.optional(),
    decisionRule: z.string().optional(),
    triageHint: z.string().optional(),
    evidence: z.array(MismatchEvidenceSchema),
    note: NonEmptyStringSchema,
    status: MismatchStatusSchema,
    classifiedBy: NonEmptyStringSchema.optional(),
    closedBy: NonEmptyStringSchema.optional(),
    closureRunId: IdSchema.optional(),
    ledgerRef: NonEmptyStringSchema.optional(),
    policyQuestionId: NonEmptyStringSchema.optional(),
    reviewBy: IsoTimestampSchema.optional(),
    acceptedBy: NonEmptyStringSchema.optional(),
    acceptedAt: IsoTimestampSchema.optional(),
    createdAt: IsoTimestampSchema
})
    .strict()
    .superRefine((m, ctx) => {
    const kinds = m.evidence.map((e) => e.kind);
    const has = (kind, count = 1) => kinds.filter((candidate) => candidate === kind).length >= count;
    const refsFor = (kind) => m.evidence
        .filter((candidate) => candidate.kind === kind)
        .map((candidate) => candidate.ref);
    const hasIndependentRefs = (kind, count) => new Set(refsFor(kind)).size >= count;
    const req = (condition, message) => {
        if (!condition) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }
    };
    if (m.classification !== "unexplained") {
        req(m.evidence.length > 0, "classified mismatch requires evidence");
        req(!!m.classifiedBy, "classified mismatch requires classifiedBy");
    }
    if (m.classification === "source_data_gap") {
        req(!!m.subtype, "source_data_gap requires subtype");
    }
    else {
        req(!m.subtype, "subtype only valid for source_data_gap");
    }
    switch (m.classification) {
        case "better_than_delivered":
            req(has("source_doc") && (has("rule_ref") || has("golden_note")), "needs source_doc + rationale ref");
            req(!!m.policyQuestionId, "needs policyQuestionId");
            req(!!m.reviewBy, "needs reviewBy");
            break;
        case "source_data_gap":
            if (m.subtype === "source_missing") {
                req(has("source_doc"), "needs source_doc naming the absent source");
            }
            if (m.subtype === "source_misextracted") {
                req(has("source_doc") && has("fixture_field"), "needs source_doc + fixture_field");
            }
            break;
        case "rule_gap":
            req(has("rule_ref") || has("golden_note"), "needs rule_ref (or documented absence)");
            break;
        case "ai_error":
            req(has("agent_trace"), "needs agent_trace");
            break;
        case "golden_error":
            req(has("source_doc") && has("golden_note"), "needs source_doc + golden_note");
            break;
        case "needs_rich_adjudication":
            req(hasIndependentRefs("source_doc", 2) ||
                (has("source_doc") && has("golden_note")) ||
                (has("source_doc") && !!m.policyQuestionId), "needs conflicting refs or source_doc + policy question");
            req(!!m.policyQuestionId, "needs policyQuestionId");
            req(!!m.reviewBy, "needs reviewBy");
            break;
        case "unexplained":
            req(has("agent_trace"), "unexplained mismatch requires agent_trace");
            break;
    }
    if (isTerminalStatus(m.status)) {
        req(!!m.closedBy, "terminal status requires closedBy");
        const allowed = {
            better_than_delivered: ["closed_adjudicated"],
            needs_rich_adjudication: ["closed_adjudicated"],
            rule_gap: ["closed_fixed"],
            ai_error: ["closed_fixed"],
            golden_error: ["closed_fixed"],
            source_data_gap: m.subtype === "source_missing"
                ? ["closed_fixed", "closed_accepted"]
                : ["closed_fixed"]
        };
        const allowedStatuses = m.classification === "unexplained" ? [] : allowed[m.classification];
        req(allowedStatuses.includes(m.status), "terminal status not allowed for this classification");
        if (m.status === "closed_fixed") {
            req(!!m.closureRunId, "closed_fixed requires closureRunId (delta absent in that run)");
        }
        if (m.status === "closed_adjudicated") {
            req(!!m.ledgerRef, "closed_adjudicated requires ledgerRef");
        }
        if (m.status === "closed_accepted") {
            req(m.classification === "source_data_gap" && m.subtype === "source_missing", "closed_accepted only valid for source_missing");
            req(!!m.ledgerRef, "closed_accepted requires ledgerRef");
            req(!!m.policyQuestionId, "closed_accepted requires policyQuestionId");
            req(!!m.acceptedBy, "closed_accepted requires acceptedBy");
            req(!!m.acceptedAt, "closed_accepted requires acceptedAt");
            req(has("golden_note"), "closed_accepted requires golden_note/README evidence");
        }
    }
});
export const PARITY_MISMATCH_LEDGER_SCHEMA_VERSION = 1;
export const ParityMismatchLedgerSchema = z
    .object({
    ledgerSchemaVersion: z.literal(PARITY_MISMATCH_LEDGER_SCHEMA_VERSION),
    runId: IdSchema,
    benchmarkCaseId: IdSchema,
    ledgerVersion: z.number().int().nonnegative(),
    ledgerHash: NonEmptyStringSchema,
    updatedAt: IsoTimestampSchema,
    entries: z.array(ParityMismatchSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    const entryIds = value.entries.map((entry) => entry.id);
    if (new Set(entryIds).size !== entryIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "mismatch ledger entry ids must be unique",
            path: ["entries"]
        });
    }
    value.entries.forEach((entry, index) => {
        if (entry.runId !== value.runId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "mismatch entry runId must match its ledger",
                path: ["entries", index, "runId"]
            });
        }
        if (entry.benchmarkCaseId !== value.benchmarkCaseId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "mismatch entry benchmarkCaseId must match its ledger",
                path: ["entries", index, "benchmarkCaseId"]
            });
        }
    });
});
export const ParityLedgerStampSchema = z
    .object({
    ledgerSchemaVersion: z.number().int().positive(),
    ledgerVersion: z.number().int().nonnegative(),
    ledgerHash: NonEmptyStringSchema
})
    .strict();
export const ApartmentTypeParityScorecardSchema = z
    .object({
    apartmentTypeId: IdSchema,
    quantity: z.number().int().positive(),
    outcome: ParityApartmentTypeOutcomeSchema.default("verified"),
    includedInCertificationDenominator: z.boolean().default(true),
    unresolvedFindingCodes: z.array(NonEmptyStringSchema).default([]),
    goldenComparable: z.boolean(),
    decisionHash: NonEmptyStringSchema,
    blockerDecisionHash: NonEmptyStringSchema,
    dimensions: z.array(ParityDimensionResultSchema),
    driftVsDeterministic: z
        .object({
        status: z.enum(["no_drift", "drift"]),
        changedDimensions: z.array(NonEmptyStringSchema)
    })
        .strict(),
    mismatchIds: z.array(IdSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    const expectedDenominatorInclusion = value.outcome === "verified";
    if (value.includedInCertificationDenominator !== expectedDenominatorInclusion) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "only verified apartment outcomes may enter certification denominators",
            path: ["includedInCertificationDenominator"]
        });
    }
    if (value.outcome === "draft_not_certified" &&
        value.unresolvedFindingCodes.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "draft/not-certified outcomes require unresolved finding codes",
            path: ["unresolvedFindingCodes"]
        });
    }
});
const ParityApartmentTypeOutcomeRecordSchema = z
    .object({
    apartmentTypeId: IdSchema,
    outcome: ParityApartmentTypeOutcomeSchema,
    quantityScored: z.boolean(),
    includedInCertificationDenominator: z.boolean(),
    traceId: IdSchema.optional(),
    unresolvedFindingCodes: z.array(NonEmptyStringSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    const expectedDenominatorInclusion = value.outcome === "verified";
    if (value.includedInCertificationDenominator !== expectedDenominatorInclusion) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "only verified apartment outcomes may enter certification denominators",
            path: ["includedInCertificationDenominator"]
        });
    }
    if (value.outcome === "draft_not_certified" &&
        value.unresolvedFindingCodes.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "draft/not-certified outcomes require unresolved finding codes",
            path: ["unresolvedFindingCodes"]
        });
    }
});
const emptyParityApartmentTypeOutcomeCounts = () => ({
    verified: 0,
    draftNotCertified: 0,
    failed: 0
});
const LegacyProjectParityScorecardCompatibilitySchema = z
    .object({
    parser: z.literal("legacy_pre_track1_compat"),
    outcomeFields: z.literal("omitted")
})
    .strict();
const isProjectParityScorecardRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const hasOwnScorecardField = (value, field) => Object.prototype.hasOwnProperty.call(value, field);
const hasEmptyLegacyOutcomeDefaults = (value) => {
    if (!Array.isArray(value.apartmentTypeOutcomes) ||
        value.apartmentTypeOutcomes.length !== 0 ||
        !isProjectParityScorecardRecord(value.outcomeCounts)) {
        return false;
    }
    return (value.outcomeCounts.verified === 0 &&
        value.outcomeCounts.draftNotCertified === 0 &&
        value.outcomeCounts.failed === 0);
};
const normalizeProjectParityScorecardForCompatibility = (value) => {
    if (!isProjectParityScorecardRecord(value)) {
        return value;
    }
    const hasApartmentTypeOutcomes = hasOwnScorecardField(value, "apartmentTypeOutcomes");
    const hasOutcomeCounts = hasOwnScorecardField(value, "outcomeCounts");
    const compatibility = isProjectParityScorecardRecord(value.artifactCompatibility)
        ? value.artifactCompatibility
        : undefined;
    const isNormalizedLegacy = compatibility?.parser === "legacy_pre_track1_compat" &&
        compatibility.outcomeFields === "omitted" &&
        hasEmptyLegacyOutcomeDefaults(value);
    if ((!hasApartmentTypeOutcomes && !hasOutcomeCounts) || isNormalizedLegacy) {
        return {
            ...value,
            artifactCompatibility: {
                parser: "legacy_pre_track1_compat",
                outcomeFields: "omitted"
            },
            apartmentTypeOutcomes: [],
            outcomeCounts: emptyParityApartmentTypeOutcomeCounts()
        };
    }
    const { artifactCompatibility: _untrustedCompatibility, ...scorecardWithoutCompatibility } = value;
    return scorecardWithoutCompatibility;
};
const ProjectParityScorecardVCurrentSchema = z
    .object({
    id: IdSchema,
    runId: IdSchema,
    benchmarkCaseId: IdSchema,
    suiteId: IdSchema,
    gateStatus: z.enum(["hard", "diagnostic"]),
    caseRunStatus: ParityCaseRunStatusSchema,
    failedApartmentTypeIds: z.array(IdSchema),
    artifactCompatibility: LegacyProjectParityScorecardCompatibilitySchema.optional(),
    apartmentTypeOutcomes: z.array(ParityApartmentTypeOutcomeRecordSchema),
    outcomeCounts: z
        .object({
        verified: z.number().int().nonnegative(),
        draftNotCertified: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative()
    })
        .strict(),
    providerId: NonEmptyStringSchema,
    model: NonEmptyStringSchema,
    aggregationMode: z.enum(["all-takeoff-rows", "quantity-expanded-takeoff-rows"]),
    inputSetHash: NonEmptyStringSchema,
    goldenHash: NonEmptyStringSchema,
    actualHash: NonEmptyStringSchema,
    hardGates: z
        .object({
        invariantsPassed: z.boolean(),
        verifiedAndJustified: z.boolean(),
        zeroLedgerViolations: z.boolean(),
        failures: z.array(NonEmptyStringSchema)
    })
        .strict(),
    dimensions: z.array(ParityDimensionResultSchema),
    apartmentTypes: z.array(ApartmentTypeParityScorecardSchema),
    mismatchIds: z.array(IdSchema),
    unexplainedCountAtRunTime: z.number().int().nonnegative(),
    usage: z
        .object({
        llmCalls: z.number().int().nonnegative(),
        promptTokens: z.number().int().nonnegative(),
        completionTokens: z.number().int().nonnegative(),
        providerFormatRetries: ProviderFormatRetryCountersSchema.default(emptyProviderFormatRetryCounters)
    })
        .strict(),
    timing: z
        .object({
        startedAt: IsoTimestampSchema,
        completedAt: IsoTimestampSchema
    })
        .strict(),
    summary: NonEmptyStringSchema
})
    .strict()
    .superRefine((value, ctx) => {
    const hasExplicitTrack1OutcomeFields = value.artifactCompatibility?.parser !== "legacy_pre_track1_compat";
    const apartmentTypeIds = value.apartmentTypes.map((apartmentType) => apartmentType.apartmentTypeId);
    const outcomeIds = value.apartmentTypeOutcomes.map((outcome) => outcome.apartmentTypeId);
    const sortedApartmentTypeIds = [...apartmentTypeIds].sort((left, right) => left.localeCompare(right));
    const sortedOutcomeIds = [...outcomeIds].sort((left, right) => left.localeCompare(right));
    if (new Set(apartmentTypeIds).size !== apartmentTypeIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "apartmentTypes must contain unique apartmentTypeId rows",
            path: ["apartmentTypes"]
        });
    }
    if (hasExplicitTrack1OutcomeFields &&
        new Set(outcomeIds).size !== outcomeIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "apartmentTypeOutcomes must contain unique apartmentTypeId rows",
            path: ["apartmentTypeOutcomes"]
        });
    }
    if (hasExplicitTrack1OutcomeFields &&
        JSON.stringify(sortedApartmentTypeIds) !== JSON.stringify(sortedOutcomeIds)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "apartmentTypes and apartmentTypeOutcomes must have exactly the same apartmentTypeId set",
            path: ["apartmentTypeOutcomes"]
        });
    }
    const outcomesByType = new Map(value.apartmentTypeOutcomes.map((outcome) => [outcome.apartmentTypeId, outcome]));
    if (hasExplicitTrack1OutcomeFields) {
        value.apartmentTypes.forEach((apartmentType, index) => {
            const outcome = outcomesByType.get(apartmentType.apartmentTypeId);
            if (outcome === undefined) {
                return;
            }
            if (apartmentType.outcome !== outcome.outcome) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "apartment outcome views must agree",
                    path: ["apartmentTypes", index, "outcome"]
                });
            }
            if (apartmentType.includedInCertificationDenominator !==
                outcome.includedInCertificationDenominator) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "apartment certification-denominator views must agree",
                    path: ["apartmentTypes", index, "includedInCertificationDenominator"]
                });
            }
            if (JSON.stringify([...apartmentType.unresolvedFindingCodes].sort()) !==
                JSON.stringify([...outcome.unresolvedFindingCodes].sort())) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "apartment unresolved-finding views must agree",
                    path: ["apartmentTypes", index, "unresolvedFindingCodes"]
                });
            }
            const quantityScored = apartmentType.dimensions.some((dimension) => dimension.scored);
            if (outcome.quantityScored !== quantityScored) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "apartment outcome quantityScored must be derived from scored dimensions",
                    path: [
                        "apartmentTypeOutcomes",
                        outcomeIds.indexOf(outcome.apartmentTypeId),
                        "quantityScored"
                    ]
                });
            }
        });
    }
    const actualCounts = {
        verified: value.apartmentTypeOutcomes.filter((outcome) => outcome.outcome === "verified").length,
        draftNotCertified: value.apartmentTypeOutcomes.filter((outcome) => outcome.outcome === "draft_not_certified").length,
        failed: value.apartmentTypeOutcomes.filter((outcome) => outcome.outcome === "failed").length
    };
    if (hasExplicitTrack1OutcomeFields &&
        (value.outcomeCounts.verified !== actualCounts.verified ||
            value.outcomeCounts.draftNotCertified !== actualCounts.draftNotCertified ||
            value.outcomeCounts.failed !== actualCounts.failed)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "outcomeCounts must exactly match apartmentTypeOutcomes",
            path: ["outcomeCounts"]
        });
    }
    if (hasExplicitTrack1OutcomeFields &&
        value.caseRunStatus === "complete" &&
        (value.apartmentTypes.length === 0 ||
            value.apartmentTypeOutcomes.length === 0 ||
            actualCounts.draftNotCertified > 0 ||
            actualCounts.failed > 0 ||
            value.apartmentTypes.some((apartmentType) => apartmentType.outcome !== "verified" ||
                !apartmentType.includedInCertificationDenominator) ||
            value.apartmentTypeOutcomes.some((outcome) => !outcome.includedInCertificationDenominator))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "complete case status is reserved for all-verified apartment outcomes",
            path: ["caseRunStatus"]
        });
    }
    const dimensionKeys = value.dimensions.map((dimension) => dimension.key);
    if (hasExplicitTrack1OutcomeFields &&
        value.caseRunStatus === "complete" &&
        (new Set(dimensionKeys).size !== dimensionKeys.length ||
            JSON.stringify([...dimensionKeys].sort()) !==
                JSON.stringify(PARITY_DIMENSION_KEYS))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "complete scorecards require exactly one row for every parity dimension",
            path: ["dimensions"]
        });
    }
    if ((actualCounts.draftNotCertified > 0 ||
        actualCounts.failed > 0 ||
        value.apartmentTypes.some((apartmentType) => apartmentType.outcome !== "verified")) &&
        value.hardGates.verifiedAndJustified) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "draft/not-certified or failed apartment outcomes cannot satisfy verifiedAndJustified",
            path: ["hardGates", "verifiedAndJustified"]
        });
    }
    const allHardGateBooleansPassed = value.hardGates.invariantsPassed &&
        value.hardGates.verifiedAndJustified &&
        value.hardGates.zeroLedgerViolations;
    if (value.hardGates.failures.length === 0 && !allHardGateBooleansPassed) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "empty hardGates.failures requires every hard-gate boolean to pass",
            path: ["hardGates", "failures"]
        });
    }
    if (hasExplicitTrack1OutcomeFields &&
        value.caseRunStatus === "complete" &&
        (!allHardGateBooleansPassed || value.hardGates.failures.length > 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "complete verified scorecards require all hard gates true and no hard-gate failures",
            path: ["hardGates"]
        });
    }
    if (new Set(value.failedApartmentTypeIds).size !== value.failedApartmentTypeIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "failedApartmentTypeIds must be unique",
            path: ["failedApartmentTypeIds"]
        });
    }
    if (new Set(value.mismatchIds).size !== value.mismatchIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "mismatchIds must be unique",
            path: ["mismatchIds"]
        });
    }
});
export const ProjectParityScorecardSchema = z.preprocess(normalizeProjectParityScorecardForCompatibility, ProjectParityScorecardVCurrentSchema);
export const ReliabilityFlakeSchema = z
    .object({
    kind: z.enum([
        "decision_flip",
        "convergence_decision_variant",
        "d7_failure",
        "provider_invalid_turn",
        "malformed_output_recovered",
        "malformed_output_fatal",
        "verifier_rejection_recovered",
        "loop_exhaustion",
        "provider_transport",
        "harness_infra"
    ]),
    benchmarkCaseId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    runIndex: z.number().int().nonnegative(),
    detail: NonEmptyStringSchema,
    agentTraceRef: NonEmptyStringSchema.optional()
})
    .strict();
const ReliabilityTupleDiffSectionSchema = z
    .object({
    changed: z.boolean(),
    before: z.unknown(),
    after: z.unknown()
})
    .strict()
    .superRefine((value, ctx) => {
    const normalize = (input) => {
        if (Array.isArray(input)) {
            return input.map(normalize);
        }
        if (typeof input === "object" && input !== null) {
            return Object.fromEntries(Object.entries(input)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([key, nested]) => [key, normalize(nested)]));
        }
        return input;
    };
    const expectedChanged = JSON.stringify(normalize(value.before)) !==
        JSON.stringify(normalize(value.after));
    if (value.changed !== expectedChanged) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "tuple-diff changed must be derived from before and after values",
            path: ["changed"]
        });
    }
});
const tupleRoleLabelMaterialSignature = (value) => {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return undefined;
    }
    const signature = {};
    for (const [key, quantity] of Object.entries(value)) {
        if (typeof quantity !== "number") {
            return undefined;
        }
        let materialKey;
        try {
            const parsed = JSON.parse(key);
            if (typeof parsed === "object" &&
                parsed !== null &&
                !Array.isArray(parsed) &&
                typeof parsed.material === "object" &&
                parsed.material !== null &&
                typeof parsed.roleLabel === "string" &&
                parsed.roleLabel !== "") {
                materialKey = JSON.stringify(Object.fromEntries(Object.entries(parsed.material).sort(([left], [right]) => left.localeCompare(right))));
            }
        }
        catch {
            const separatorIndex = key.lastIndexOf(":");
            if (separatorIndex > 0 && separatorIndex < key.length - 1) {
                materialKey = key.slice(0, separatorIndex);
            }
        }
        if (materialKey === undefined) {
            return undefined;
        }
        signature[materialKey] = (signature[materialKey] ?? 0) + quantity;
    }
    return Object.fromEntries(Object.entries(signature).sort(([left], [right]) => left.localeCompare(right)));
};
const tupleSectionIsRoleRelabelOnly = (before, after) => {
    const beforeSignature = tupleRoleLabelMaterialSignature(before);
    const afterSignature = tupleRoleLabelMaterialSignature(after);
    return (beforeSignature !== undefined &&
        afterSignature !== undefined &&
        Object.keys(beforeSignature).length > 0 &&
        JSON.stringify(beforeSignature) === JSON.stringify(afterSignature));
};
const ReliabilityTupleDiffSchema = z
    .object({
    apartmentTypeId: IdSchema,
    fromRunIndex: z.number().int().nonnegative(),
    toRunIndex: z.number().int().nonnegative(),
    fromDecisionHash: NonEmptyStringSchema,
    toDecisionHash: NonEmptyStringSchema,
    fromBlockerDecisionHash: NonEmptyStringSchema,
    toBlockerDecisionHash: NonEmptyStringSchema,
    fromControllerDecisionHash: NonEmptyStringSchema,
    toControllerDecisionHash: NonEmptyStringSchema,
    classification: z.enum([
        "controller_topology",
        "role_relabel",
        "non_controller_decision_surface",
        "validation_status",
        "risk_surface"
    ]),
    sections: z
        .object({
        controllers: ReliabilityTupleDiffSectionSchema,
        devices: ReliabilityTupleDiffSectionSchema,
        infrastructure: ReliabilityTupleDiffSectionSchema,
        infrastructureMirrorDemands: ReliabilityTupleDiffSectionSchema.default({
            changed: false,
            before: {},
            after: {}
        }),
        pointLedger: ReliabilityTupleDiffSectionSchema,
        risks: ReliabilityTupleDiffSectionSchema,
        validationStatus: ReliabilityTupleDiffSectionSchema
    })
        .strict()
})
    .strict()
    .superRefine((value, ctx) => {
    const sections = value.sections;
    const expectedClassification = sections.controllers.changed
        ? "controller_topology"
        : sections.validationStatus.changed
            ? "validation_status"
            : sections.risks.changed
                ? "risk_surface"
                : (sections.devices.changed || sections.infrastructure.changed) &&
                    !sections.pointLedger.changed &&
                    !sections.infrastructureMirrorDemands.changed &&
                    (!sections.devices.changed ||
                        tupleSectionIsRoleRelabelOnly(sections.devices.before, sections.devices.after)) &&
                    (!sections.infrastructure.changed ||
                        tupleSectionIsRoleRelabelOnly(sections.infrastructure.before, sections.infrastructure.after))
                    ? "role_relabel"
                    : "non_controller_decision_surface";
    if (value.classification !== expectedClassification) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "tuple-diff classification must be derived from section rows",
            path: ["classification"]
        });
    }
});
const ReliabilityConvergenceFailureKindSchema = z.enum([
    "decision_variant",
    "d7_failure",
    "provider_invalid_turn"
]);
const ReliabilityConvergenceFailureSchema = z
    .object({
    kind: ReliabilityConvergenceFailureKindSchema,
    benchmarkCaseId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    runIndex: z.number().int().nonnegative(),
    detail: NonEmptyStringSchema,
    agentTraceRef: NonEmptyStringSchema.optional()
})
    .strict();
const ReliabilityConvergenceAttemptSchema = z
    .object({
    apartmentTypeId: IdSchema,
    triggeredByRunIndex: z.number().int().nonnegative(),
    baselineRunIndex: z.number().int().nonnegative(),
    priorDecisionHash: NonEmptyStringSchema,
    currentDecisionHash: NonEmptyStringSchema,
    convergenceRunIndex: z.number().int().nonnegative(),
    outcomeDecisionHash: NonEmptyStringSchema.optional(),
    outcomeControllerDecisionHash: NonEmptyStringSchema.optional(),
    changedFromCurrent: z.boolean().optional(),
    changedFromPrior: z.boolean().optional(),
    citedReferences: z.array(NonEmptyStringSchema),
    traceId: IdSchema.optional()
})
    .strict();
const ReliabilityCertificationCriterionSchema = z
    .object({
    id: z.enum([
        "config_fingerprint_frozen",
        "full_success_every_project_every_run",
        "d7_pass_every_emitted_type",
        "d7_source_provenance_zero_or_blocker_classified",
        "zero_point_ledger_violations",
        "zero_material_decision_flips",
        "zero_material_controller_infrastructure_flips",
        "zero_provider_invalid_turns",
        "zero_reference_fallback",
        "structural_no_forbidden_paths",
        "agentic_loop_evidence_present",
        "partial_runs_quarantined"
    ]),
    passed: z.boolean(),
    detail: NonEmptyStringSchema
})
    .strict();
const ReliabilityAnchorStateSummarySchema = z
    .object({
    observedTypeCount: z.number().int().nonnegative(),
    consistentTypeCount: z.number().int().nonnegative(),
    decisionConsistencyRate: z.number().min(0).max(1)
})
    .strict();
const ReliabilityQuarantineReasonKindSchema = z.enum([
    "draft_not_certified",
    "partial_run_preserved",
    "partial_run_failure",
    "global_aggregator_blocker",
    "trace_blocked",
    "trace_failed",
    "failed_run"
]);
const ReliabilityQuarantinedApartmentRunSchema = z
    .object({
    runIndex: z.number().int().nonnegative(),
    apartmentTypeId: IdSchema,
    reasonKind: ReliabilityQuarantineReasonKindSchema,
    detail: NonEmptyStringSchema,
    traceId: IdSchema.optional(),
    aggregatorRoundIndex: z.number().int().positive().optional(),
    aggregatorOutcomeStatus: z.enum(["verified", "failed", "blocked"]).optional(),
    siblingPatternCitation: z
        .object({
        apartmentTypeIds: z.array(IdSchema).min(1),
        summary: NonEmptyStringSchema
    })
        .strict()
        .optional(),
    outlierReasons: z.array(NonEmptyStringSchema).default([]),
    evidenceRefs: z.array(NonEmptyStringSchema).default([])
})
    .strict();
const ReliabilityAgenticToolEvidenceSchema = z
    .object({
    source: z.enum(["agent_tool", "engine_tool", "verifier_result"]),
    toolName: NonEmptyStringSchema,
    verdict: NonEmptyStringSchema,
    status: NonEmptyStringSchema,
    violationCount: z.number().int().nonnegative().default(0),
    findingCodes: z.array(NonEmptyStringSchema).default([]),
    evidenceRef: NonEmptyStringSchema.optional()
})
    .strict();
const ReliabilityRecheckPromptSchema = z
    .object({
    source: z.enum(["agent_tool", "convergence_context", "global_aggregator"]),
    prompt: NonEmptyStringSchema,
    evidenceRef: NonEmptyStringSchema.optional()
})
    .strict();
const ReliabilityAggregatorInterventionSchema = z
    .object({
    runIndex: z.number().int().nonnegative(),
    apartmentTypeId: IdSchema,
    roundIndex: z.number().int().positive(),
    providerId: NonEmptyStringSchema.optional(),
    verdict: NonEmptyStringSchema,
    outlierReasons: z.array(NonEmptyStringSchema).default([]),
    siblingPatternCitation: z
        .object({
        apartmentTypeIds: z.array(IdSchema).min(1),
        summary: NonEmptyStringSchema
    })
        .strict()
        .optional(),
    recheckPrompt: NonEmptyStringSchema.optional(),
    outcomeStatus: z.enum(["verified", "failed", "blocked"]).optional(),
    outcomeTraceId: IdSchema.optional(),
    outcomeMessage: NonEmptyStringSchema.optional(),
    evidenceRef: NonEmptyStringSchema.optional()
})
    .strict();
const ReliabilityPerApartmentRunEvidenceSchema = z
    .object({
    runIndex: z.number().int().nonnegative(),
    apartmentTypeId: IdSchema,
    traceId: IdSchema.optional(),
    // Track 1: `draft` records a materialized but NOT CERTIFIED apartment
    // result. Reliability evidence must be able to say that plainly rather
    // than reporting it as verified or as a missing trace.
    traceStatus: z.enum(["verified", "blocked", "draft", "failed", "missing_trace"]),
    finalConvergenceState: z.enum([
        "converged",
        "quarantined_blocker",
        "quarantined_partial",
        "draft",
        "blocked",
        "failed",
        "missing_trace"
    ]),
    toolLoopRetriesUsed: z.number().int().nonnegative(),
    advisoryWarningCodes: z.array(NonEmptyStringSchema).default([]),
    tools: z.array(ReliabilityAgenticToolEvidenceSchema),
    recheckPrompts: z.array(ReliabilityRecheckPromptSchema),
    aggregatorInterventions: z.array(ReliabilityAggregatorInterventionSchema),
    finalVerifiedSolutionRef: NonEmptyStringSchema.optional(),
    finalCandidateSolutionRef: NonEmptyStringSchema.optional(),
    unresolvedFindingCodes: z.array(NonEmptyStringSchema).default([]),
    quarantined: z.boolean(),
    quarantineReasonKind: ReliabilityQuarantineReasonKindSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.traceStatus === "draft") {
        if (value.finalCandidateSolutionRef === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draft reliability rows require a candidate solution reference",
                path: ["finalCandidateSolutionRef"]
            });
        }
        if (value.finalVerifiedSolutionRef !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draft reliability rows must never carry a verified solution reference",
                path: ["finalVerifiedSolutionRef"]
            });
        }
        if (!value.quarantined || value.quarantineReasonKind !== "draft_not_certified") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draft reliability rows must be quarantined as draft_not_certified",
                path: ["quarantineReasonKind"]
            });
        }
        if (value.unresolvedFindingCodes.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draft reliability rows require unresolved finding codes",
                path: ["unresolvedFindingCodes"]
            });
        }
    }
    else if (value.finalCandidateSolutionRef !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "candidate solution references are reserved for draft rows",
            path: ["finalCandidateSolutionRef"]
        });
    }
    if (value.traceStatus === "verified" &&
        value.finalVerifiedSolutionRef === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "verified reliability rows require a verified solution reference",
            path: ["finalVerifiedSolutionRef"]
        });
    }
    if (value.traceStatus !== "verified" &&
        value.finalVerifiedSolutionRef !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "only verified reliability rows may carry a verified solution reference",
            path: ["finalVerifiedSolutionRef"]
        });
    }
    if (value.quarantined !== (value.quarantineReasonKind !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "quarantined must agree with quarantineReasonKind",
            path: ["quarantined"]
        });
    }
    const expectedFinalConvergenceState = value.traceStatus === "draft"
        ? "draft"
        : value.quarantineReasonKind === "partial_run_preserved"
            ? "quarantined_partial"
            : value.quarantined
                ? "quarantined_blocker"
                : value.traceStatus === "verified"
                    ? "converged"
                    : value.traceStatus;
    if (value.finalConvergenceState !== expectedFinalConvergenceState) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "finalConvergenceState must be derived from traceStatus and quarantine state",
            path: ["finalConvergenceState"]
        });
    }
});
const reliabilityAgenticLoopFinalState = (rows) => rows.length === 0
    ? "no_evidence"
    : rows.some((run) => ["failed", "missing_trace", "quarantined_partial"].includes(run.finalConvergenceState))
        ? "partial_or_failed"
        : rows.some((run) => run.traceStatus === "draft")
            ? "draft_not_certified"
            : rows.some((run) => ["blocked", "quarantined_blocker"].includes(run.finalConvergenceState))
                ? "blocked_or_quarantined"
                : "all_converged";
const ReliabilityAgenticLoopEvidenceSchema = z
    .object({
    summary: NonEmptyStringSchema,
    finalConvergenceState: z.enum([
        "all_converged",
        "draft_not_certified",
        "blocked_or_quarantined",
        "partial_or_failed",
        "no_evidence"
    ]),
    apartmentRunCount: z.number().int().nonnegative(),
    toolCallCount: z.number().int().nonnegative(),
    toolLoopRetryCount: z.number().int().nonnegative(),
    recheckPromptCount: z.number().int().nonnegative(),
    aggregatorInterventionCount: z.number().int().nonnegative(),
    perApartmentRuns: z.array(ReliabilityPerApartmentRunEvidenceSchema),
    aggregatorInterventions: z.array(ReliabilityAggregatorInterventionSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    const expected = {
        finalConvergenceState: reliabilityAgenticLoopFinalState(value.perApartmentRuns),
        apartmentRunCount: value.perApartmentRuns.length,
        toolCallCount: value.perApartmentRuns.reduce((total, run) => total + run.tools.length, 0),
        toolLoopRetryCount: value.perApartmentRuns.reduce((total, run) => total + run.toolLoopRetriesUsed, 0),
        recheckPromptCount: value.perApartmentRuns.reduce((total, run) => total + run.recheckPrompts.length, 0),
        aggregatorInterventionCount: value.perApartmentRuns.reduce((total, run) => total + run.aggregatorInterventions.length, 0)
    };
    for (const [field, expectedValue] of Object.entries(expected)) {
        if (value[field] !== expectedValue) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field} must be derived from perApartmentRuns`,
                path: [field]
            });
        }
    }
    const flattenedInterventions = value.perApartmentRuns.flatMap((run) => run.aggregatorInterventions);
    if (JSON.stringify(value.aggregatorInterventions) !==
        JSON.stringify(flattenedInterventions)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "agenticLoop.aggregatorInterventions must be the exact flattened per-row interventions",
            path: ["aggregatorInterventions"]
        });
    }
    value.perApartmentRuns.forEach((run, runIndex) => {
        run.aggregatorInterventions.forEach((intervention, interventionIndex) => {
            if (intervention.runIndex !== run.runIndex ||
                intervention.apartmentTypeId !== run.apartmentTypeId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "aggregator intervention identity must match its apartment-run row",
                    path: [
                        "perApartmentRuns",
                        runIndex,
                        "aggregatorInterventions",
                        interventionIndex
                    ]
                });
            }
        });
    });
});
const ReliabilityForbiddenPathKindSchema = z.enum([
    "deterministic_override",
    "reference_fallback",
    "post_hoc_normalization",
    "modal_answer_pass"
]);
const ReliabilityForbiddenPathFindingSchema = z
    .object({
    kind: ReliabilityForbiddenPathKindSchema,
    evidenceRef: NonEmptyStringSchema,
    excerpt: NonEmptyStringSchema,
    runIndex: z.number().int().nonnegative().optional(),
    apartmentTypeId: IdSchema.optional(),
    traceId: IdSchema.optional()
})
    .strict();
const ReliabilityStructuralNoOverrideAssertionSchema = z
    .object({
    passed: z.boolean(),
    scannedTraceCount: z.number().int().nonnegative(),
    scannedDecisionCount: z.number().int().nonnegative(),
    decisionMaterialization: z
        .object({
        passed: z.boolean(),
        standaloneAcceptedDecisionCount: z.number().int().nonnegative(),
        providerPayloadAcceptedDecisionCount: z.number().int().nonnegative(),
        missingStandaloneDecisionIds: z.array(IdSchema)
    })
        .strict()
        .default({
        passed: true,
        standaloneAcceptedDecisionCount: 0,
        providerPayloadAcceptedDecisionCount: 0,
        missingStandaloneDecisionIds: []
    }),
    forbiddenPathFindings: z.array(ReliabilityForbiddenPathFindingSchema),
    forbiddenPaths: z
        .object({
        deterministicOverride: z.boolean(),
        referenceFallback: z.boolean(),
        postHocNormalization: z.boolean(),
        modalAnswerPass: z.boolean()
    })
        .strict(),
    detail: NonEmptyStringSchema
})
    .strict()
    .superRefine((value, ctx) => {
    const expectedDecisionMaterializationPassed = value.decisionMaterialization.missingStandaloneDecisionIds.length === 0;
    if (value.decisionMaterialization.passed !== expectedDecisionMaterializationPassed) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "decisionMaterialization.passed must be derived from missing decision ids",
            path: ["decisionMaterialization", "passed"]
        });
    }
    const expectedForbiddenPaths = {
        deterministicOverride: value.forbiddenPathFindings.some((finding) => finding.kind === "deterministic_override"),
        referenceFallback: value.forbiddenPathFindings.some((finding) => finding.kind === "reference_fallback"),
        postHocNormalization: value.forbiddenPathFindings.some((finding) => finding.kind === "post_hoc_normalization"),
        modalAnswerPass: value.forbiddenPathFindings.some((finding) => finding.kind === "modal_answer_pass")
    };
    for (const [field, expected] of Object.entries(expectedForbiddenPaths)) {
        if (value.forbiddenPaths[field] !== expected) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field} must be derived from forbiddenPathFindings`,
                path: ["forbiddenPaths", field]
            });
        }
    }
    const expectedPassed = value.forbiddenPathFindings.length === 0 &&
        value.scannedTraceCount > 0 &&
        expectedDecisionMaterializationPassed;
    if (value.passed !== expectedPassed) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "structural no-override passed must be derived from trace and finding evidence",
            path: ["passed"]
        });
    }
});
const ReliabilityHumanSummarySchema = z
    .object({
    artifactSchema: NonEmptyStringSchema,
    certification: NonEmptyStringSchema,
    warningDisclosure: NonEmptyStringSchema.default("No certified advisory warning-code disclosure was recorded."),
    materialDecisions: NonEmptyStringSchema,
    agenticLoop: NonEmptyStringSchema,
    noOverride: NonEmptyStringSchema,
    quarantine: NonEmptyStringSchema
})
    .strict();
const ReliabilityWarningCodeSetSampleSchema = z
    .object({
    runIndex: z.number().int().nonnegative(),
    apartmentTypeId: IdSchema,
    warningCodes: z.array(NonEmptyStringSchema),
    traceId: IdSchema.optional()
})
    .strict();
const ReliabilityWarningCodeSetVariantSchema = z
    .object({
    warningCodes: z.array(NonEmptyStringSchema),
    runIndexes: z.array(z.number().int().nonnegative()).min(1)
})
    .strict();
const ReliabilityAdvisoryWarningVarianceSchema = z
    .object({
    typeCount: z.number().int().nonnegative(),
    variants: z.array(z
        .object({
        apartmentTypeId: IdSchema,
        warningSets: z.array(ReliabilityWarningCodeSetVariantSchema).min(2)
    })
        .strict())
})
    .strict();
const ReliabilityWarningCodeSetDisclosureSchema = z
    .object({
    certifiedSampleCount: z.number().int().nonnegative(),
    advisorySampleCount: z.number().int().nonnegative(),
    warningCodeUniverse: z.array(NonEmptyStringSchema),
    perSample: z.array(ReliabilityWarningCodeSetSampleSchema),
    advisoryVariance: ReliabilityAdvisoryWarningVarianceSchema
})
    .strict();
const ReliabilityAgenticArchitectureSchema = z
    .object({
    agentToolLoopEnabled: z.boolean(),
    globalAggregatorEnabled: z.boolean()
})
    .strict();
const ReliabilityGlobalAggregatorRunEvidenceSchema = z
    .object({
    present: z.boolean(),
    enabled: z.boolean(),
    roundCount: z.number().int().nonnegative(),
    finalState: z
        .enum(["skipped", "all_consistent", "converged", "blocked", "errored"])
        .optional(),
    message: NonEmptyStringSchema.optional()
})
    .strict();
const ReliabilityArtifactCompatibilitySchema = z
    .object({
    sourceSchemaVersion: z.number().int().positive(),
    parser: z.enum(["native_v2", "legacy_v1_compat"]),
    reportedAs: z.enum(["native", "legacy"])
})
    .strict();
const ReliabilityCertificationScopeSchema = z
    .object({
    kind: z.enum([
        "agentic_recertification",
        "legacy_non_agentic",
        "mixed_agentic_state"
    ]),
    agenticRecertificationEligible: z.boolean(),
    reason: NonEmptyStringSchema
})
    .strict();
const ReliabilityReportV2Schema = z
    .object({
    schemaVersion: z.literal(2),
    id: IdSchema,
    benchmarkCaseId: IdSchema,
    artifactCompatibility: ReliabilityArtifactCompatibilitySchema,
    certificationScope: ReliabilityCertificationScopeSchema,
    agenticArchitecture: ReliabilityAgenticArchitectureSchema,
    configFingerprint: NonEmptyStringSchema,
    bestEffortDraftEnabled: z.boolean().default(false),
    draftScoringProtocolVersion: NonEmptyStringSchema.default("legacy-unrecorded-draft-scoring-protocol"),
    providerCallArchitecture: NonEmptyStringSchema,
    providerFormatRetries: ProviderFormatRetryCountersSchema.default(emptyProviderFormatRetryCounters),
    runsPlanned: z.number().int().positive(),
    runsCompleted: z.number().int().nonnegative(),
    decisionHashesByType: z.record(IdSchema, z.array(NonEmptyStringSchema)),
    blockerDecisionHashesByType: z.record(IdSchema, z.array(NonEmptyStringSchema)),
    consistentTypeCount: z.number().int().nonnegative(),
    totalTypeCount: z.number().int().positive(),
    decisionConsistencyRate: z.number().min(0).max(1),
    perRunConfigFingerprints: z.array(z
        .object({
        runIndex: z.number().int().nonnegative(),
        configFingerprint: NonEmptyStringSchema,
        bestEffortDraftEnabled: z.boolean().default(false),
        draftScoringProtocolVersion: NonEmptyStringSchema.default("legacy-unrecorded-draft-scoring-protocol"),
        providerCallArchitecture: NonEmptyStringSchema,
        agenticArchitecture: ReliabilityAgenticArchitectureSchema,
        globalAggregatorEvidence: ReliabilityGlobalAggregatorRunEvidenceSchema
    })
        .strict()),
    certificationSample: z
        .object({
        certifiedRunCount: z.number().int().nonnegative(),
        certifiedTypeCount: z.number().int().nonnegative(),
        preservedPartialRunCount: z.number().int().nonnegative(),
        preservedPartialTypeCount: z.number().int().nonnegative(),
        draftRunCount: z.number().int().nonnegative().default(0),
        draftTypeCount: z.number().int().nonnegative().default(0),
        failedRunCount: z.number().int().nonnegative(),
        excludedRunIndexes: z.array(z.number().int().nonnegative()),
        quarantinedApartmentRunCount: z.number().int().nonnegative().default(0),
        quarantinedApartmentRuns: z
            .array(ReliabilityQuarantinedApartmentRunSchema)
            .default([])
    })
        .strict(),
    warningCodeSetDisclosure: ReliabilityWarningCodeSetDisclosureSchema.default({
        certifiedSampleCount: 0,
        advisorySampleCount: 0,
        warningCodeUniverse: [],
        perSample: [],
        advisoryVariance: {
            typeCount: 0,
            variants: []
        }
    }),
    d7: z
        .object({
        certifiedEmittedTypeCount: z.number().int().nonnegative(),
        certifiedPassedTypeCount: z.number().int().nonnegative(),
        certifiedPassRate: z.number().min(0).max(1),
        preservedPartialEmittedTypeCount: z.number().int().nonnegative(),
        blockerClassifiedFailureCount: z.number().int().nonnegative().default(0)
    })
        .strict(),
    coverageByRun: z.array(z
        .object({
        runIndex: z.number().int().nonnegative(),
        caseRunStatus: z.enum([
            "complete",
            "draft_not_certified",
            "partial",
            "failed_to_run"
        ]),
        scoredTypeCount: z.number().int().nonnegative(),
        verifiedTypeCount: z.number().int().nonnegative().default(0),
        draftTypeCount: z.number().int().nonnegative().default(0),
        failedTypeCount: z.number().int().nonnegative().default(0),
        failedApartmentTypeIds: z.array(IdSchema)
    })
        .strict()),
    coverageVariance: z
        .object({
        expectedTypeCount: z.number().int().positive(),
        minScoredTypeCount: z.number().int().nonnegative(),
        maxScoredTypeCount: z.number().int().nonnegative(),
        draftRunCount: z.number().int().nonnegative().default(0),
        partialRunCount: z.number().int().nonnegative(),
        failedRunCount: z.number().int().nonnegative()
    })
        .strict(),
    controllerDecisionFlips: z.number().int().nonnegative(),
    infrastructureDecisionFlips: z.number().int().nonnegative().default(0),
    materialDecisionFlips: z.number().int().nonnegative().default(0),
    blockerDecisionFlips: z.number().int().nonnegative(),
    tupleDiffs: z.array(ReliabilityTupleDiffSchema),
    convergenceFailures: z.array(ReliabilityConvergenceFailureSchema),
    convergenceFailureCounts: z
        .object({
        decisionVariant: z.number().int().nonnegative(),
        d7Failure: z.number().int().nonnegative(),
        providerInvalidTurn: z.number().int().nonnegative()
    })
        .strict(),
    convergenceAttempts: z.array(ReliabilityConvergenceAttemptSchema),
    failureRateUpperBound95: z.number().min(0).max(1),
    cumulativeRuns: z.number().int().nonnegative(),
    cumulativeFailureRateUpperBound95: z.number().min(0).max(1),
    anchorProvenance: z
        .object({
        byRun: z.array(z
            .object({
            runIndex: z.number().int().nonnegative(),
            apartmentTypeIds: z.array(IdSchema),
            anchoredApartmentTypeIds: z.array(IdSchema),
            unanchoredApartmentTypeIds: z.array(IdSchema),
            freshAnchorApartmentTypeIds: z.array(IdSchema),
            staleAnchorApartmentTypeIds: z.array(IdSchema),
            incomparableAnchorApartmentTypeIds: z.array(IdSchema)
        })
            .strict()),
        decisionConsistencyByAnchorState: z
            .object({
            unanchored: ReliabilityAnchorStateSummarySchema,
            anchored: ReliabilityAnchorStateSummarySchema
        })
            .strict()
    })
        .strict()
        .optional(),
    modalDisagreements: z.array(z
        .object({
        apartmentTypeId: IdSchema,
        modalHash: NonEmptyStringSchema,
        modalSupport: z.number().int().positive(),
        variants: z.array(NonEmptyStringSchema)
    })
        .strict()),
    agenticLoop: ReliabilityAgenticLoopEvidenceSchema.default({
        summary: "Legacy artifact did not carry per-apartment agentic loop evidence.",
        finalConvergenceState: "no_evidence",
        apartmentRunCount: 0,
        toolCallCount: 0,
        toolLoopRetryCount: 0,
        recheckPromptCount: 0,
        aggregatorInterventionCount: 0,
        perApartmentRuns: [],
        aggregatorInterventions: []
    }),
    structuralNoOverrideAssertion: ReliabilityStructuralNoOverrideAssertionSchema.default({
        passed: false,
        scannedTraceCount: 0,
        scannedDecisionCount: 0,
        forbiddenPathFindings: [],
        forbiddenPaths: {
            deterministicOverride: false,
            referenceFallback: false,
            postHocNormalization: false,
            modalAnswerPass: false
        },
        decisionMaterialization: {
            passed: true,
            standaloneAcceptedDecisionCount: 0,
            providerPayloadAcceptedDecisionCount: 0,
            missingStandaloneDecisionIds: []
        },
        detail: "Legacy artifact did not carry structural trace evidence for the no-override assertion."
    }),
    humanSummary: ReliabilityHumanSummarySchema.default({
        artifactSchema: "reliability-report-v1-legacy",
        certification: "Legacy artifact parsed through the v2 compatibility schema.",
        warningDisclosure: "Legacy artifact did not carry certified advisory warning-code disclosure.",
        materialDecisions: "Legacy artifact did not separately summarize material flips.",
        agenticLoop: "Legacy artifact did not carry per-apartment agentic loop evidence.",
        noOverride: "Legacy artifact did not carry a structural no-override assertion.",
        quarantine: "Legacy artifact used pre-v2 quarantine fields only."
    }),
    flakes: z.array(ReliabilityFlakeSchema),
    certification: z
        .object({
        criteria: z.array(ReliabilityCertificationCriterionSchema),
        passed: z.boolean()
    })
        .strict(),
    passed: z.boolean()
})
    .strict()
    .superRefine((value, ctx) => {
    const issue = (message, path) => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path });
    };
    if (value.artifactCompatibility.parser === "legacy_v1_compat") {
        const legacyClassificationIsConsistent = value.artifactCompatibility.sourceSchemaVersion === 1 &&
            value.artifactCompatibility.reportedAs === "legacy" &&
            value.certificationScope.kind === "legacy_non_agentic" &&
            value.certificationScope.agenticRecertificationEligible === false &&
            value.agenticArchitecture.agentToolLoopEnabled === false &&
            value.agenticArchitecture.globalAggregatorEnabled === false;
        const legacyPerRunArchitectureIsConsistent = value.perRunConfigFingerprints.every((entry) => entry.agenticArchitecture.agentToolLoopEnabled === false &&
            entry.agenticArchitecture.globalAggregatorEnabled === false &&
            entry.globalAggregatorEvidence.present === false &&
            entry.globalAggregatorEvidence.enabled === false &&
            entry.globalAggregatorEvidence.roundCount === 0);
        const legacyAgenticEvidenceIsEmpty = value.agenticLoop.finalConvergenceState === "no_evidence" &&
            value.agenticLoop.apartmentRunCount === 0 &&
            value.agenticLoop.toolCallCount === 0 &&
            value.agenticLoop.toolLoopRetryCount === 0 &&
            value.agenticLoop.recheckPromptCount === 0 &&
            value.agenticLoop.aggregatorInterventionCount === 0 &&
            value.agenticLoop.perApartmentRuns.length === 0 &&
            value.agenticLoop.aggregatorInterventions.length === 0;
        const legacyStructuralEvidenceIsEmpty = value.structuralNoOverrideAssertion.passed === false &&
            value.structuralNoOverrideAssertion.scannedTraceCount === 0 &&
            value.structuralNoOverrideAssertion.scannedDecisionCount === 0 &&
            value.structuralNoOverrideAssertion.forbiddenPathFindings.length === 0 &&
            !Object.values(value.structuralNoOverrideAssertion.forbiddenPaths).some(Boolean) &&
            value.structuralNoOverrideAssertion.decisionMaterialization
                .standaloneAcceptedDecisionCount === 0 &&
            value.structuralNoOverrideAssertion.decisionMaterialization
                .providerPayloadAcceptedDecisionCount === 0 &&
            value.structuralNoOverrideAssertion.decisionMaterialization
                .missingStandaloneDecisionIds.length === 0;
        const legacyWarningDisclosureIsEmpty = value.warningCodeSetDisclosure.certifiedSampleCount === 0 &&
            value.warningCodeSetDisclosure.advisorySampleCount === 0 &&
            value.warningCodeSetDisclosure.warningCodeUniverse.length === 0 &&
            value.warningCodeSetDisclosure.perSample.length === 0 &&
            value.warningCodeSetDisclosure.advisoryVariance.typeCount === 0 &&
            value.warningCodeSetDisclosure.advisoryVariance.variants.length === 0;
        if (!legacyClassificationIsConsistent) {
            issue("legacy v1 compatibility classification must remain non-agentic", [
                "artifactCompatibility"
            ]);
        }
        if (!legacyPerRunArchitectureIsConsistent) {
            issue("legacy v1 compatibility rows cannot carry agentic architecture", [
                "perRunConfigFingerprints"
            ]);
        }
        if (!legacyAgenticEvidenceIsEmpty) {
            issue("legacy v1 compatibility cannot carry native agentic-loop evidence", [
                "agenticLoop"
            ]);
        }
        if (!legacyStructuralEvidenceIsEmpty) {
            issue("legacy v1 compatibility cannot carry native structural evidence", [
                "structuralNoOverrideAssertion"
            ]);
        }
        if (!legacyWarningDisclosureIsEmpty) {
            issue("legacy v1 compatibility cannot carry native warning disclosure", [
                "warningCodeSetDisclosure"
            ]);
        }
        return;
    }
    const sameNumbers = (left, right) => JSON.stringify([...left].sort((a, b) => a - b)) ===
        JSON.stringify([...right].sort((a, b) => a - b));
    const sameStrings = (left, right) => JSON.stringify([...left].sort((a, b) => a.localeCompare(b))) ===
        JSON.stringify([...right].sort((a, b) => a.localeCompare(b)));
    const nearlyEqual = (left, right) => Math.abs(left - right) <= Number.EPSILON * 16;
    const plannedRunIndexes = Array.from({ length: value.runsPlanned }, (_unused, runIndex) => runIndex);
    const fingerprintRunIndexes = value.perRunConfigFingerprints.map((entry) => entry.runIndex);
    if (new Set(fingerprintRunIndexes).size !== fingerprintRunIndexes.length ||
        !sameNumbers(fingerprintRunIndexes, plannedRunIndexes)) {
        issue("perRunConfigFingerprints must contain exactly one row for every planned run", ["perRunConfigFingerprints"]);
    }
    value.perRunConfigFingerprints.forEach((entry, index) => {
        const expectedFields = {
            configFingerprint: value.configFingerprint,
            bestEffortDraftEnabled: value.bestEffortDraftEnabled,
            draftScoringProtocolVersion: value.draftScoringProtocolVersion,
            providerCallArchitecture: value.providerCallArchitecture
        };
        for (const [field, expected] of Object.entries(expectedFields)) {
            if (entry[field] !== expected) {
                issue(`${field} must agree between the report and every per-run fingerprint row`, ["perRunConfigFingerprints", index, field]);
            }
        }
        if (entry.agenticArchitecture.agentToolLoopEnabled !==
            value.agenticArchitecture.agentToolLoopEnabled ||
            entry.agenticArchitecture.globalAggregatorEnabled !==
                value.agenticArchitecture.globalAggregatorEnabled) {
            issue("agenticArchitecture must agree between the report and every per-run fingerprint row", ["perRunConfigFingerprints", index, "agenticArchitecture"]);
        }
    });
    const coverageRunIndexes = value.coverageByRun.map((coverage) => coverage.runIndex);
    if (new Set(coverageRunIndexes).size !== coverageRunIndexes.length) {
        issue("coverageByRun runIndex rows must be unique", ["coverageByRun"]);
    }
    if (coverageRunIndexes.some((runIndex) => runIndex < 0 || runIndex >= value.runsPlanned)) {
        issue("coverageByRun rows must refer to planned run indexes", ["coverageByRun"]);
    }
    if (value.runsCompleted !== value.coverageByRun.length) {
        issue("runsCompleted must equal coverageByRun.length", ["runsCompleted"]);
    }
    const coverageRunIndexSet = new Set(coverageRunIndexes);
    const baseRows = value.agenticLoop.perApartmentRuns.filter((row) => coverageRunIndexSet.has(row.runIndex));
    const apartmentRunKeys = value.agenticLoop.perApartmentRuns.map((row) => `${row.runIndex}:${row.apartmentTypeId}`);
    if (new Set(apartmentRunKeys).size !== apartmentRunKeys.length) {
        issue("perApartmentRuns must contain unique run/type rows", [
            "agenticLoop",
            "perApartmentRuns"
        ]);
    }
    const rowsByRun = new Map();
    for (const row of baseRows) {
        const rows = rowsByRun.get(row.runIndex) ?? [];
        rows.push(row);
        rowsByRun.set(row.runIndex, rows);
    }
    value.coverageByRun.forEach((coverage, index) => {
        const rows = rowsByRun.get(coverage.runIndex) ?? [];
        const expectedCounts = {
            verifiedTypeCount: rows.filter((row) => row.traceStatus === "verified").length,
            draftTypeCount: rows.filter((row) => row.traceStatus === "draft").length,
            failedTypeCount: rows.filter((row) => ["blocked", "failed", "missing_trace"].includes(row.traceStatus)).length
        };
        const expectedScoredTypeCount = expectedCounts.verifiedTypeCount + expectedCounts.draftTypeCount;
        const expectedFailedApartmentTypeIds = rows
            .filter((row) => ["blocked", "failed", "missing_trace"].includes(row.traceStatus))
            .map((row) => row.apartmentTypeId)
            .sort((left, right) => left.localeCompare(right));
        for (const [field, expected] of Object.entries(expectedCounts)) {
            if (coverage[field] !== expected) {
                issue(`${field} must be derived from perApartmentRuns`, [
                    "coverageByRun",
                    index,
                    field
                ]);
            }
        }
        if (coverage.scoredTypeCount !== expectedScoredTypeCount) {
            issue("scoredTypeCount must equal materialized verified and draft rows", [
                "coverageByRun",
                index,
                "scoredTypeCount"
            ]);
        }
        if (!sameStrings(coverage.failedApartmentTypeIds, expectedFailedApartmentTypeIds) ||
            new Set(coverage.failedApartmentTypeIds).size !==
                coverage.failedApartmentTypeIds.length) {
            issue("failedApartmentTypeIds must identify exactly the failed evidence rows", [
                "coverageByRun",
                index,
                "failedApartmentTypeIds"
            ]);
        }
        if (coverage.caseRunStatus === "complete" &&
            (rows.length === 0 ||
                expectedCounts.draftTypeCount > 0 ||
                expectedCounts.failedTypeCount > 0)) {
            issue("complete coverage rows require a nonempty all-verified apartment population", ["coverageByRun", index, "caseRunStatus"]);
        }
        if (coverage.caseRunStatus === "draft_not_certified" &&
            expectedCounts.draftTypeCount === 0) {
            issue("draft coverage rows require draft apartment evidence", [
                "coverageByRun",
                index,
                "caseRunStatus"
            ]);
        }
    });
    const certifiedRunIndexes = value.coverageByRun
        .filter((coverage) => coverage.caseRunStatus === "complete")
        .map((coverage) => coverage.runIndex);
    const draftRunIndexes = new Set([
        ...value.coverageByRun
            .filter((coverage) => coverage.caseRunStatus === "draft_not_certified")
            .map((coverage) => coverage.runIndex),
        ...value.agenticLoop.perApartmentRuns
            .filter((row) => row.traceStatus === "draft")
            .map((row) => row.runIndex)
    ]);
    const quarantinedRows = value.agenticLoop.perApartmentRuns.filter((row) => row.quarantined);
    const quarantinedRowKeys = quarantinedRows.map((row) => `${row.runIndex}:${row.apartmentTypeId}`);
    const quarantineSummaryKeys = value.certificationSample.quarantinedApartmentRuns.map((row) => `${row.runIndex}:${row.apartmentTypeId}`);
    const failedRunCount = value.runsPlanned -
        value.coverageByRun.length +
        value.coverageByRun.filter((coverage) => coverage.caseRunStatus === "failed_to_run").length;
    const expectedCertificationSample = {
        certifiedRunCount: certifiedRunIndexes.length,
        certifiedTypeCount: baseRows.filter((row) => row.traceStatus === "verified" && !row.quarantined).length,
        preservedPartialRunCount: value.coverageByRun.filter((coverage) => coverage.caseRunStatus === "partial").length,
        preservedPartialTypeCount: value.coverageByRun
            .filter((coverage) => coverage.caseRunStatus === "partial")
            .reduce((total, coverage) => total + coverage.scoredTypeCount, 0),
        draftRunCount: draftRunIndexes.size,
        draftTypeCount: value.agenticLoop.perApartmentRuns.filter((row) => row.traceStatus === "draft").length,
        failedRunCount,
        quarantinedApartmentRunCount: quarantinedRows.length
    };
    for (const [field, expected] of Object.entries(expectedCertificationSample)) {
        if (value.certificationSample[field] !==
            expected) {
            issue(`${field} must be derived from coverage and apartment-run rows`, [
                "certificationSample",
                field
            ]);
        }
    }
    const expectedExcludedRunIndexes = plannedRunIndexes.filter((runIndex) => !certifiedRunIndexes.includes(runIndex));
    if (!sameNumbers(value.certificationSample.excludedRunIndexes, expectedExcludedRunIndexes)) {
        issue("excludedRunIndexes must be the complement of certified run indexes", [
            "certificationSample",
            "excludedRunIndexes"
        ]);
    }
    if (new Set(quarantineSummaryKeys).size !== quarantineSummaryKeys.length ||
        !sameStrings(quarantineSummaryKeys, quarantinedRowKeys)) {
        issue("quarantinedApartmentRuns must correspond exactly to quarantined apartment rows", ["certificationSample", "quarantinedApartmentRuns"]);
    }
    const quarantinedRowsByKey = new Map(quarantinedRows.map((row) => [`${row.runIndex}:${row.apartmentTypeId}`, row]));
    value.certificationSample.quarantinedApartmentRuns.forEach((quarantine, index) => {
        const row = quarantinedRowsByKey.get(`${quarantine.runIndex}:${quarantine.apartmentTypeId}`);
        if (row !== undefined &&
            (quarantine.reasonKind !== row.quarantineReasonKind ||
                quarantine.traceId !== row.traceId)) {
            issue("quarantine summaries must agree with apartment-row reason and trace evidence", ["certificationSample", "quarantinedApartmentRuns", index]);
        }
    });
    const scoredCoverageCounts = value.coverageByRun.map((coverage) => coverage.scoredTypeCount);
    const expectedCoverageVariance = {
        minScoredTypeCount: scoredCoverageCounts.length > 0 ? Math.min(...scoredCoverageCounts) : 0,
        maxScoredTypeCount: scoredCoverageCounts.length > 0 ? Math.max(...scoredCoverageCounts) : 0,
        draftRunCount: draftRunIndexes.size,
        partialRunCount: expectedCertificationSample.preservedPartialRunCount,
        failedRunCount
    };
    for (const [field, expected] of Object.entries(expectedCoverageVariance)) {
        if (value.coverageVariance[field] !==
            expected) {
            issue(`${field} must be derived from coverageByRun`, [
                "coverageVariance",
                field
            ]);
        }
    }
    const expectedCoverageTypeCount = Math.max(1, Object.keys(value.decisionHashesByType).length, ...value.coverageByRun.map((coverage) => coverage.scoredTypeCount + coverage.failedApartmentTypeIds.length));
    if (value.coverageVariance.expectedTypeCount !== expectedCoverageTypeCount) {
        issue("expectedTypeCount must be derived from decision and coverage rows", [
            "coverageVariance",
            "expectedTypeCount"
        ]);
    }
    const traceRowCount = value.agenticLoop.perApartmentRuns.filter((row) => row.traceId !== undefined).length;
    if (value.structuralNoOverrideAssertion.scannedTraceCount !== traceRowCount) {
        issue("scannedTraceCount must match the unique trace evidence carried by apartment rows", ["structuralNoOverrideAssertion", "scannedTraceCount"]);
    }
    const certifiedRunIndexSet = new Set(certifiedRunIndexes);
    const certifiedDecisionRowCounts = new Map();
    for (const row of baseRows) {
        if (!certifiedRunIndexSet.has(row.runIndex) || row.quarantined) {
            continue;
        }
        certifiedDecisionRowCounts.set(row.apartmentTypeId, (certifiedDecisionRowCounts.get(row.apartmentTypeId) ?? 0) + 1);
    }
    const expectedDecisionTypeIds = [...certifiedDecisionRowCounts.keys()].sort((left, right) => left.localeCompare(right));
    const decisionTypeIds = Object.keys(value.decisionHashesByType).sort((left, right) => left.localeCompare(right));
    const blockerDecisionTypeIds = Object.keys(value.blockerDecisionHashesByType).sort((left, right) => left.localeCompare(right));
    if (!sameStrings(decisionTypeIds, expectedDecisionTypeIds) ||
        !sameStrings(blockerDecisionTypeIds, expectedDecisionTypeIds)) {
        issue("decision hash maps must identify exactly the certified apartment-row population", ["decisionHashesByType"]);
    }
    for (const [apartmentTypeId, expectedCount] of certifiedDecisionRowCounts) {
        if (value.decisionHashesByType[apartmentTypeId]?.length !== expectedCount ||
            value.blockerDecisionHashesByType[apartmentTypeId]?.length !== expectedCount) {
            issue("decision hash arrays must contain one value per certified run/type row", ["decisionHashesByType", apartmentTypeId]);
        }
    }
    const inconsistentTypeIds = decisionTypeIds.filter((apartmentTypeId) => new Set(value.decisionHashesByType[apartmentTypeId] ?? []).size > 1);
    const expectedConsistentTypeCount = decisionTypeIds.length - inconsistentTypeIds.length;
    const expectedTotalTypeCount = Math.max(1, decisionTypeIds.length);
    if (value.consistentTypeCount !== expectedConsistentTypeCount) {
        issue("consistentTypeCount must be derived from decisionHashesByType", [
            "consistentTypeCount"
        ]);
    }
    if (value.totalTypeCount !== expectedTotalTypeCount) {
        issue("totalTypeCount must be derived from decisionHashesByType", [
            "totalTypeCount"
        ]);
    }
    if (!nearlyEqual(value.decisionConsistencyRate, expectedConsistentTypeCount / expectedTotalTypeCount)) {
        issue("decisionConsistencyRate must be derived from decision hash rows", [
            "decisionConsistencyRate"
        ]);
    }
    if (!sameStrings(value.modalDisagreements.map((disagreement) => disagreement.apartmentTypeId), inconsistentTypeIds)) {
        issue("modalDisagreements must identify exactly the varying decision types", [
            "modalDisagreements"
        ]);
    }
    value.modalDisagreements.forEach((disagreement, index) => {
        const hashes = value.decisionHashesByType[disagreement.apartmentTypeId] ?? [];
        const counts = new Map();
        for (const hash of hashes) {
            counts.set(hash, (counts.get(hash) ?? 0) + 1);
        }
        const modal = [...counts.entries()]
            .map(([hash, support]) => ({ hash, support }))
            .sort((left, right) => right.support - left.support || left.hash.localeCompare(right.hash))[0];
        const variants = [...counts.keys()].sort((left, right) => left.localeCompare(right));
        if (modal === undefined ||
            disagreement.modalHash !== modal.hash ||
            disagreement.modalSupport !== modal.support ||
            !sameStrings(disagreement.variants, variants)) {
            issue("modal disagreement summaries must be derived from decision hashes", [
                "modalDisagreements",
                index
            ]);
        }
    });
    const expectedBlockerDecisionFlips = Object.values(value.blockerDecisionHashesByType).filter((hashes) => new Set(hashes).size > 1).length;
    if (value.blockerDecisionFlips !== expectedBlockerDecisionFlips) {
        issue("blockerDecisionFlips must be derived from blockerDecisionHashesByType", [
            "blockerDecisionFlips"
        ]);
    }
    const materialTupleDiffs = value.tupleDiffs.filter((diff) => diff.classification !== "role_relabel");
    const expectedMaterialDecisionFlips = materialTupleDiffs.filter((diff) => diff.sections.controllers.changed ||
        diff.sections.devices.changed ||
        diff.sections.infrastructure.changed ||
        diff.sections.infrastructureMirrorDemands.changed).length;
    const expectedInfrastructureDecisionFlips = materialTupleDiffs.filter((diff) => diff.sections.infrastructure.changed).length;
    const expectedControllerDecisionFlips = new Set(materialTupleDiffs
        .filter((diff) => diff.sections.controllers.changed)
        .map((diff) => diff.apartmentTypeId)).size;
    if (value.controllerDecisionFlips !== expectedControllerDecisionFlips) {
        issue("controllerDecisionFlips must be derived from tupleDiff rows", [
            "controllerDecisionFlips"
        ]);
    }
    if (value.materialDecisionFlips !== expectedMaterialDecisionFlips) {
        issue("materialDecisionFlips must be derived from tupleDiffs", [
            "materialDecisionFlips"
        ]);
    }
    if (value.infrastructureDecisionFlips !== expectedInfrastructureDecisionFlips) {
        issue("infrastructureDecisionFlips must be derived from tupleDiffs", [
            "infrastructureDecisionFlips"
        ]);
    }
    const expectedConvergenceFailureCounts = {
        decisionVariant: value.convergenceFailures.filter((failure) => failure.kind === "decision_variant").length,
        d7Failure: value.convergenceFailures.filter((failure) => failure.kind === "d7_failure").length,
        providerInvalidTurn: value.convergenceFailures.filter((failure) => failure.kind === "provider_invalid_turn").length
    };
    for (const [field, expected] of Object.entries(expectedConvergenceFailureCounts)) {
        if (value.convergenceFailureCounts[field] !== expected) {
            issue(`${field} must be derived from convergenceFailures`, [
                "convergenceFailureCounts",
                field
            ]);
        }
    }
    value.convergenceFailures.forEach((failure, index) => {
        if (failure.benchmarkCaseId !== value.benchmarkCaseId) {
            issue("convergence failure case identity must match the report", [
                "convergenceFailures",
                index,
                "benchmarkCaseId"
            ]);
        }
        if (failure.apartmentTypeId !== undefined &&
            !value.agenticLoop.perApartmentRuns.some((row) => row.runIndex === failure.runIndex &&
                row.apartmentTypeId === failure.apartmentTypeId)) {
            issue("typed convergence failures require a matching apartment-run row", [
                "convergenceFailures",
                index,
                "apartmentTypeId"
            ]);
        }
    });
    const quarantineKeySet = new Set(quarantinedRowKeys);
    const certifiedD7FailureCount = value.convergenceFailures.filter((failure) => failure.kind === "d7_failure" &&
        certifiedRunIndexSet.has(failure.runIndex) &&
        (failure.apartmentTypeId === undefined ||
            !quarantineKeySet.has(`${failure.runIndex}:${failure.apartmentTypeId}`))).length;
    const blockerClassifiedD7FailureCount = value.convergenceFailures.filter((failure) => failure.kind === "d7_failure" &&
        failure.apartmentTypeId !== undefined &&
        quarantineKeySet.has(`${failure.runIndex}:${failure.apartmentTypeId}`)).length;
    const expectedD7 = {
        certifiedEmittedTypeCount: expectedCertificationSample.certifiedTypeCount,
        certifiedPassedTypeCount: Math.max(0, expectedCertificationSample.certifiedTypeCount - certifiedD7FailureCount),
        preservedPartialEmittedTypeCount: expectedCertificationSample.preservedPartialTypeCount,
        blockerClassifiedFailureCount: blockerClassifiedD7FailureCount
    };
    for (const [field, expected] of Object.entries(expectedD7)) {
        if (value.d7[field] !== expected) {
            issue(`${field} must be derived from certification evidence rows`, [
                "d7",
                field
            ]);
        }
    }
    const expectedCertifiedPassRate = expectedD7.certifiedEmittedTypeCount === 0
        ? 0
        : expectedD7.certifiedPassedTypeCount / expectedD7.certifiedEmittedTypeCount;
    if (!nearlyEqual(value.d7.certifiedPassRate, expectedCertifiedPassRate)) {
        issue("certifiedPassRate must be derived from D7 counts", [
            "d7",
            "certifiedPassRate"
        ]);
    }
    const failureRateUpperBound95 = (runs) => runs <= 0 ? 1 : 1 - Math.pow(0.05, 1 / runs);
    if (!nearlyEqual(value.failureRateUpperBound95, failureRateUpperBound95(expectedCertificationSample.certifiedRunCount))) {
        issue("failureRateUpperBound95 must be derived from certifiedRunCount", [
            "failureRateUpperBound95"
        ]);
    }
    if (!nearlyEqual(value.cumulativeFailureRateUpperBound95, failureRateUpperBound95(value.cumulativeRuns))) {
        issue("cumulativeFailureRateUpperBound95 must be derived from cumulativeRuns", [
            "cumulativeFailureRateUpperBound95"
        ]);
    }
    const perRunAgenticEnabled = value.perRunConfigFingerprints.length > 0 &&
        value.perRunConfigFingerprints.every((entry) => entry.agenticArchitecture.agentToolLoopEnabled &&
            entry.agenticArchitecture.globalAggregatorEnabled);
    const perRunLegacy = value.perRunConfigFingerprints.length > 0 &&
        value.perRunConfigFingerprints.every((entry) => !entry.agenticArchitecture.agentToolLoopEnabled &&
            !entry.agenticArchitecture.globalAggregatorEnabled);
    const completedSurvivorsByRun = new Map();
    for (const row of value.agenticLoop.perApartmentRuns) {
        if (row.traceStatus !== "verified") {
            continue;
        }
        const survivors = completedSurvivorsByRun.get(row.runIndex) ?? new Set();
        survivors.add(row.apartmentTypeId);
        completedSurvivorsByRun.set(row.runIndex, survivors);
    }
    const aggregatorEvidencePresent = value.perRunConfigFingerprints.every((entry) => {
        const evidence = entry.globalAggregatorEvidence;
        if (!evidence.present || !evidence.enabled) {
            return false;
        }
        if (evidence.roundCount > 0 && evidence.finalState !== "skipped") {
            return true;
        }
        const survivorCount = completedSurvivorsByRun.get(entry.runIndex)?.size ?? 0;
        return (evidence.roundCount === 0 &&
            evidence.finalState === "skipped" &&
            (evidence.message?.trim().length ?? 0) > 0 &&
            survivorCount < 2);
    });
    const everyRowHasAgenticToolEvidence = value.agenticLoop.perApartmentRuns.length > 0 &&
        value.agenticLoop.perApartmentRuns.every((row) => row.tools.some((tool) => tool.source === "agent_tool" || tool.source === "engine_tool"));
    const agenticEvidencePassed = perRunAgenticEnabled &&
        value.agenticLoop.toolCallCount > 0 &&
        value.agenticLoop.perApartmentRuns.every((row) => row.traceStatus === "verified") &&
        everyRowHasAgenticToolEvidence &&
        aggregatorEvidencePresent;
    const expectedScopeKind = perRunAgenticEnabled
        ? "agentic_recertification"
        : perRunLegacy
            ? "legacy_non_agentic"
            : "mixed_agentic_state";
    if (value.certificationScope.kind !== expectedScopeKind) {
        issue("certificationScope.kind must be derived from per-run architecture", [
            "certificationScope",
            "kind"
        ]);
    }
    if (value.certificationScope.agenticRecertificationEligible !==
        (expectedScopeKind === "agentic_recertification" && agenticEvidencePassed)) {
        issue("agenticRecertificationEligible must be derived from per-run evidence", [
            "certificationScope",
            "agenticRecertificationEligible"
        ]);
    }
    const criteriaById = new Map(value.certification.criteria.map((criterion) => [criterion.id, criterion]));
    if (criteriaById.size !== value.certification.criteria.length) {
        issue("certification criterion ids must be unique", [
            "certification",
            "criteria"
        ]);
    }
    const requiredCriterionIds = [
        "config_fingerprint_frozen",
        "full_success_every_project_every_run",
        "d7_pass_every_emitted_type",
        "d7_source_provenance_zero_or_blocker_classified",
        "zero_point_ledger_violations",
        "zero_material_decision_flips",
        "zero_material_controller_infrastructure_flips",
        "zero_provider_invalid_turns",
        "zero_reference_fallback",
        "structural_no_forbidden_paths",
        ...(expectedScopeKind === "legacy_non_agentic"
            ? []
            : ["agentic_loop_evidence_present"]),
        "partial_runs_quarantined"
    ];
    if (!sameStrings([...criteriaById.keys()], requiredCriterionIds)) {
        issue("certification criteria must contain the exact required criterion set", [
            "certification",
            "criteria"
        ]);
    }
    const allNonVerifiedRowsQuarantined = value.agenticLoop.perApartmentRuns
        .filter((row) => row.traceStatus !== "verified")
        .every((row) => row.quarantined);
    const derivedCriteria = {
        config_fingerprint_frozen: true,
        full_success_every_project_every_run: expectedCertificationSample.certifiedRunCount === value.runsPlanned &&
            expectedCertificationSample.quarantinedApartmentRunCount === 0,
        d7_pass_every_emitted_type: certifiedD7FailureCount === 0,
        d7_source_provenance_zero_or_blocker_classified: certifiedD7FailureCount === 0,
        zero_material_decision_flips: expectedMaterialDecisionFlips === 0,
        zero_material_controller_infrastructure_flips: expectedMaterialDecisionFlips === 0,
        zero_provider_invalid_turns: expectedConvergenceFailureCounts.providerInvalidTurn === 0,
        zero_reference_fallback: !value.structuralNoOverrideAssertion.forbiddenPaths.referenceFallback,
        structural_no_forbidden_paths: value.structuralNoOverrideAssertion.passed,
        agentic_loop_evidence_present: agenticEvidencePassed,
        partial_runs_quarantined: allNonVerifiedRowsQuarantined
    };
    for (const [criterionId, expected] of Object.entries(derivedCriteria)) {
        const criterion = criteriaById.get(criterionId);
        if (criterion !== undefined && criterion.passed !== expected) {
            issue(`${criterionId}.passed must be derived from evidence rows`, [
                "certification",
                "criteria",
                value.certification.criteria.indexOf(criterion),
                "passed"
            ]);
        }
    }
    const expectedCertificationPassed = value.certification.criteria.every((criterion) => criterion.passed);
    if (value.certification.passed !== expectedCertificationPassed) {
        issue("certification.passed must be derived from certification criteria", [
            "certification",
            "passed"
        ]);
    }
    const blockingConvergenceFailureCount = value.convergenceFailures.filter((failure) => failure.apartmentTypeId === undefined ||
        !quarantineKeySet.has(`${failure.runIndex}:${failure.apartmentTypeId}`)).length;
    if (value.passed !==
        (expectedCertificationPassed &&
            value.flakes.length === 0 &&
            blockingConvergenceFailureCount === 0)) {
        issue("report passed must be derived from certification and flake rows", [
            "passed"
        ]);
    }
});
const LEGACY_PROVIDER_CALL_ARCHITECTURE = "legacy-unrecorded-provider-call-architecture";
const LEGACY_AGENTIC_ARCHITECTURE = {
    agentToolLoopEnabled: false,
    globalAggregatorEnabled: false
};
const LEGACY_GLOBAL_AGGREGATOR_EVIDENCE = {
    present: false,
    enabled: false,
    roundCount: 0
};
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const nonEmptyStringValue = (value) => typeof value === "string" && value.length > 0 ? value : undefined;
const normalizeNativeReliabilityReportV2 = (value) => {
    const agenticLoop = isRecord(value.agenticLoop) ? value.agenticLoop : undefined;
    const perApartmentRuns = Array.isArray(agenticLoop?.perApartmentRuns)
        ? agenticLoop.perApartmentRuns.filter(isRecord)
        : [];
    const rowsByRun = new Map();
    for (const row of perApartmentRuns) {
        if (typeof row.runIndex !== "number" || !Number.isInteger(row.runIndex)) {
            continue;
        }
        const rows = rowsByRun.get(row.runIndex) ?? [];
        rows.push(row);
        rowsByRun.set(row.runIndex, rows);
    }
    const coverageByRun = Array.isArray(value.coverageByRun)
        ? value.coverageByRun.map((coverage) => {
            if (!isRecord(coverage) ||
                typeof coverage.runIndex !== "number" ||
                !Number.isInteger(coverage.runIndex)) {
                return coverage;
            }
            const rows = rowsByRun.get(coverage.runIndex) ?? [];
            const count = (statuses) => rows.filter((row) => typeof row.traceStatus === "string" && statuses.includes(row.traceStatus)).length;
            return {
                ...coverage,
                verifiedTypeCount: typeof coverage.verifiedTypeCount === "number"
                    ? coverage.verifiedTypeCount
                    : count(["verified"]),
                draftTypeCount: typeof coverage.draftTypeCount === "number"
                    ? coverage.draftTypeCount
                    : count(["draft"]),
                failedTypeCount: typeof coverage.failedTypeCount === "number"
                    ? coverage.failedTypeCount
                    : count(["blocked", "failed", "missing_trace"])
            };
        })
        : value.coverageByRun;
    return {
        ...value,
        artifactCompatibility: {
            sourceSchemaVersion: 2,
            parser: "native_v2",
            reportedAs: "native"
        },
        coverageByRun
    };
};
const normalizeReliabilityReportForCompatibility = (value) => {
    if (!isRecord(value)) {
        return value;
    }
    const compatibility = isRecord(value.artifactCompatibility)
        ? value.artifactCompatibility
        : undefined;
    const isNormalizedLegacyV1 = value.schemaVersion === 2 &&
        compatibility?.sourceSchemaVersion === 1 &&
        compatibility.parser === "legacy_v1_compat" &&
        compatibility.reportedAs === "legacy";
    if (isNormalizedLegacyV1) {
        return value;
    }
    if (value.schemaVersion === 2) {
        return normalizeNativeReliabilityReportV2(value);
    }
    const sourceSchemaVersion = typeof value.schemaVersion === "number" && Number.isInteger(value.schemaVersion)
        ? value.schemaVersion
        : 1;
    const providerCallArchitecture = nonEmptyStringValue(value.providerCallArchitecture) ??
        LEGACY_PROVIDER_CALL_ARCHITECTURE;
    const perRunConfigFingerprints = Array.isArray(value.perRunConfigFingerprints)
        ? value.perRunConfigFingerprints.map((entry) => isRecord(entry)
            ? {
                ...entry,
                providerCallArchitecture: nonEmptyStringValue(entry.providerCallArchitecture) ??
                    providerCallArchitecture,
                agenticArchitecture: isRecord(entry.agenticArchitecture) &&
                    typeof entry.agenticArchitecture.agentToolLoopEnabled === "boolean" &&
                    typeof entry.agenticArchitecture.globalAggregatorEnabled === "boolean"
                    ? entry.agenticArchitecture
                    : LEGACY_AGENTIC_ARCHITECTURE,
                globalAggregatorEvidence: isRecord(entry.globalAggregatorEvidence) &&
                    typeof entry.globalAggregatorEvidence.present === "boolean" &&
                    typeof entry.globalAggregatorEvidence.enabled === "boolean" &&
                    typeof entry.globalAggregatorEvidence.roundCount === "number"
                    ? entry.globalAggregatorEvidence
                    : LEGACY_GLOBAL_AGGREGATOR_EVIDENCE
            }
            : entry)
        : value.perRunConfigFingerprints;
    return {
        ...value,
        schemaVersion: 2,
        artifactCompatibility: {
            sourceSchemaVersion,
            parser: "legacy_v1_compat",
            reportedAs: "legacy"
        },
        certificationScope: {
            kind: "legacy_non_agentic",
            agenticRecertificationEligible: false,
            reason: "Legacy reliability artifact parsed without recorded agentic loop enabled state; usable only as a non-agentic certification."
        },
        agenticArchitecture: LEGACY_AGENTIC_ARCHITECTURE,
        providerCallArchitecture,
        perRunConfigFingerprints
    };
};
export const ReliabilityReportSchema = z.preprocess(normalizeReliabilityReportForCompatibility, ReliabilityReportV2Schema);
export const CorpusParityRollupSchema = z
    .object({
    id: IdSchema,
    runId: IdSchema,
    createdAt: IsoTimestampSchema,
    ledgerStampsByCase: z.record(IdSchema, ParityLedgerStampSchema),
    ledgerCorpusHash: NonEmptyStringSchema,
    recomputedAt: IsoTimestampSchema,
    caseScorecardIds: z.array(IdSchema),
    casesPassed: z.number().int().nonnegative(),
    casesTotal: z.number().int().positive(),
    perDimension: z.record(NonEmptyStringSchema, z
        .object({
        meanExactLineRate: z.number().min(0).max(1),
        meanQuantityOverlapRate: z.number().min(0).max(1),
        casesMeetingBar: z.number().int().nonnegative()
    })
        .strict()),
    mismatchCountsByClassification: z.record(MismatchClassificationSchema, z.number().int().nonnegative()),
    mismatchCountsByStatus: z.record(MismatchStatusSchema, z.number().int().nonnegative()),
    unexplainedCount: z.number().int().nonnegative(),
    nonTerminalCount: z.number().int().nonnegative(),
    staleAdjudicationCount: z.number().int().nonnegative(),
    adjudicationLoadByCase: z.record(IdSchema, z.number().min(0)),
    terminalAdjudicationLoadByCase: z.record(IdSchema, z.number().min(0)),
    explainedAndClosedParityRate: z.number().min(0).max(1),
    reliability: z
        .object({
        reportIds: z.array(IdSchema),
        corpusDecisionConsistencyRate: z.number().min(0).max(1),
        controllerDecisionFlips: z.number().int().nonnegative(),
        cumulativeRuns: z.number().int().nonnegative(),
        cumulativeFailureRateUpperBound95: z.number().min(0).max(1)
    })
        .strict()
        .optional(),
    passed: z.boolean(),
    summary: NonEmptyStringSchema
})
    .strict()
    .superRefine((value, ctx) => {
    const issue = (message, path) => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path });
    };
    if (new Set(value.caseScorecardIds).size !== value.caseScorecardIds.length) {
        issue("caseScorecardIds must be unique", ["caseScorecardIds"]);
    }
    if (value.casesTotal !== value.caseScorecardIds.length) {
        issue("casesTotal must equal caseScorecardIds.length", ["casesTotal"]);
    }
    if (value.casesPassed > value.casesTotal) {
        issue("casesPassed cannot exceed casesTotal", ["casesPassed"]);
    }
    const classificationTotal = Object.values(value.mismatchCountsByClassification).reduce((total, count) => total + count, 0);
    const statusTotal = Object.values(value.mismatchCountsByStatus).reduce((total, count) => total + count, 0);
    if (classificationTotal !== statusTotal) {
        issue("mismatch classification and status totals must describe the same entries", ["mismatchCountsByStatus"]);
    }
    if (value.unexplainedCount !== value.mismatchCountsByClassification.unexplained) {
        issue("unexplainedCount must be derived from mismatch classifications", [
            "unexplainedCount"
        ]);
    }
    const terminalCount = TERMINAL_MISMATCH_STATUSES.reduce((total, status) => total + (value.mismatchCountsByStatus[status] ?? 0), 0);
    if (value.nonTerminalCount !== statusTotal - terminalCount) {
        issue("nonTerminalCount must be derived from mismatch statuses", [
            "nonTerminalCount"
        ]);
    }
    const expectedExplainedAndClosedRate = statusTotal === 0 ? 1 : terminalCount / statusTotal;
    if (Math.abs(value.explainedAndClosedParityRate - expectedExplainedAndClosedRate) >
        Number.EPSILON * 16) {
        issue("explainedAndClosedParityRate must be derived from mismatch status rows", [
            "explainedAndClosedParityRate"
        ]);
    }
    if (Object.values(value.perDimension).some((dimension) => dimension.casesMeetingBar > value.casesTotal)) {
        issue("per-dimension casesMeetingBar cannot exceed casesTotal", ["perDimension"]);
    }
    if (value.reliability !== undefined &&
        new Set(value.reliability.reportIds).size !== value.reliability.reportIds.length) {
        issue("reliability reportIds must be unique", ["reliability", "reportIds"]);
    }
    const expectedPassed = value.casesPassed === value.casesTotal &&
        value.unexplainedCount === 0 &&
        value.nonTerminalCount === 0 &&
        value.staleAdjudicationCount === 0 &&
        Object.values(value.adjudicationLoadByCase).every((load) => load <= 0.2) &&
        (value.reliability === undefined ||
            value.reliability.controllerDecisionFlips === 0);
    if (value.passed !== expectedPassed) {
        issue("rollup passed must be derived from rollup evidence aggregates", [
            "passed"
        ]);
    }
});
//# sourceMappingURL=parity.js.map